import React, { useState, useEffect } from 'react';
import { XMarkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { usePlans } from '../hooks/usePlans';
import moment from 'moment';

/**
 * Modal to view/edit company details
 */
export function CompanyDetailsModal({ company, isOpen, onClose, onUpdate }) {
  const { plans, listByDatabase, listConsolidated } = usePlans();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(company || {});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && company) {
      setFormData(company);
      // Load plans from the company's database
      if (company._database?.id && company._database?.id !== 'consolidated') {
        listByDatabase(company._database.id);
      } else {
        listConsolidated();
      }
    }
  }, [isOpen, company, listByDatabase, listConsolidated]);

  if (!isOpen || !company) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onUpdate?.(formData);
      setEditMode(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanName = () => {
    if (formData.plan?.name) return formData.plan.name;
    if (formData.planName) return formData.planName;
    return '-';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <BuildingOfficeIcon className="w-7 h-7 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">{company.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!editMode ? (
            /* View Mode */
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-base font-semibold text-gray-900">ID</label>
                  <p className="mt-2 text-base text-gray-700">{company.id}</p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Status</label>
                  <p className="mt-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                        company.status
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {company.status ? 'Ativo' : 'Inativo'}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Email</label>
                  <p className="mt-2 text-base text-gray-700">{company.email || '-'}</p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Telefone</label>
                  <p className="mt-2 text-base text-gray-700">{company.phone || '-'}</p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">CPF/CNPJ</label>
                  <p className="mt-2 text-base text-gray-700">{company.cpfCnpj || '-'}</p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Plano</label>
                  <p className="mt-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                      {getPlanName()}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Data de Vencimento</label>
                  <p className="mt-2 text-base text-gray-700">
                    {company.dueDate ? moment.utc(company.dueDate).format('DD/MM/YYYY') : '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Recorrência</label>
                  <p className="mt-2 text-base text-gray-700">{company.recurrence || '-'}</p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Banco de Dados</label>
                  <p className="mt-2 text-base text-gray-700">{company._database?.displayName || company._database?.name || '-'}</p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900">Criado em</label>
                  <p className="mt-2 text-base text-gray-700">
                    {company.createdAt ? moment(company.createdAt).format('DD/MM/YYYY HH:mm') : '-'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Nome</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Telefone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">CPF/CNPJ</label>
                  <input
                    type="text"
                    name="cpfCnpj"
                    value={formData.cpfCnpj || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Plano</label>
                  <select
                    name="planId"
                    value={formData.planId || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Selecione um plano</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Data de Vencimento</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate ? moment.utc(formData.dueDate).format('YYYY-MM-DD') : ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Recorrência</label>
                  <select
                    name="recurrence"
                    value={formData.recurrence || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Sem recorrência</option>
                    <option value="MENSAL">Mensal</option>
                    <option value="BIMESTRAL">Bimestral</option>
                    <option value="TRIMESTRAL">Trimestral</option>
                    <option value="SEMESTRAL">Semestral</option>
                    <option value="ANUAL">Anual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-2">Status</label>
                  <select
                    name="status"
                    value={formData.status ? 'true' : 'false'}
                    onChange={(e) =>
                      handleChange({
                        target: { name: 'status', value: e.target.value === 'true' },
                      })
                    }
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end p-6 border-t border-gray-200 sticky bottom-0 bg-gray-50">
          {editMode ? (
            <>
              <button
                onClick={() => setEditMode(false)}
                disabled={isLoading}
                className="px-5 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-5 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 text-base font-semibold"
              >
                {isLoading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-5 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
              >
                Fechar
              </button>
              <button
                onClick={() => setEditMode(true)}
                className="px-5 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-base font-semibold"
              >
                Editar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
